import { formatCount, formatPercent } from "../format";

export interface BarItem {
  key: string;
  label: string;
  value: number;
  /** Share of the whole, 0–1. Falls back to value/max when absent. */
  share?: number;
  /** Right-aligned secondary metric, e.g. an activation rate. */
  meta?: string;
  /** Tailwind background class for the bar fill. */
  color?: string;
}

/**
 * Horizontal bar list. Bars are sized against the largest item rather than the
 * total, so a long tail stays readable; the printed figure is the real share.
 */
export function BarList({
  items,
  onSelect,
  emptyMessage = "No data",
}: {
  items: BarItem[];
  onSelect?: (item: BarItem) => void;
  emptyMessage?: string;
}) {
  if (items.length === 0) {
    return (
      <div className="text-subtle-foreground px-4 py-8 text-center text-sm">
        {emptyMessage}
      </div>
    );
  }

  const max = Math.max(...items.map((item) => item.value), 1);

  return (
    <ul className="divide-border divide-y">
      {items.map((item) => {
        const width = `${Math.max((item.value / max) * 100, 1.5)}%`;
        const body = (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-foreground truncate text-sm">
                {item.label}
              </span>
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                {item.meta ? `${item.meta} · ` : ""}
                {formatCount(item.value)}
                {item.share !== undefined && (
                  <span className="text-subtle-foreground">
                    {" "}
                    ({formatPercent(item.share)})
                  </span>
                )}
              </span>
            </div>
            <div className="bg-muted mt-1.5 h-1.5 overflow-hidden rounded-full">
              <div
                className={`h-full rounded-full ${item.color ?? "bg-blue-500"}`}
                style={{ width }}
              />
            </div>
          </>
        );

        return (
          <li key={item.key}>
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(item)}
                className="hover:bg-muted w-full cursor-pointer px-4 py-2.5 text-left"
              >
                {body}
              </button>
            ) : (
              <div className="px-4 py-2.5">{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export interface TrendPoint {
  key: string;
  label: string;
  value: number;
}

/** Widest series that still leaves a legible column per month. */
const MAX_TREND_POINTS = 18;

/**
 * Column chart for a monthly series. Only the most recent months are drawn —
 * the series grows by one column a month forever, and past this many the
 * columns compress to unreadable slivers.
 */
export function TrendBars({
  points,
  onSelect,
}: {
  points: TrendPoint[];
  onSelect?: (point: TrendPoint) => void;
}) {
  const visible = points.slice(-MAX_TREND_POINTS);

  if (visible.length === 0) {
    return (
      <div className="text-subtle-foreground px-4 py-8 text-center text-sm">
        No signups recorded
      </div>
    );
  }

  const max = Math.max(...visible.map((point) => point.value), 1);

  return (
    <div className="flex items-end gap-2 overflow-x-auto px-4 pt-6 pb-4">
      {visible.map((point) => (
        <button
          key={point.key}
          type="button"
          disabled={!onSelect}
          onClick={() => onSelect?.(point)}
          className="group flex min-w-7 flex-1 cursor-pointer flex-col items-center gap-1.5 disabled:cursor-default"
          title={`${point.label}: ${formatCount(point.value)}`}
        >
          <span className="text-muted-foreground text-xs tabular-nums">
            {formatCount(point.value)}
          </span>
          <span
            className="w-full rounded-t bg-blue-500 transition-colors group-hover:bg-blue-600 group-disabled:bg-blue-500"
            style={{ height: `${Math.max((point.value / max) * 96, 3)}px` }}
          />
          <span className="text-subtle-foreground text-xs">{point.label}</span>
        </button>
      ))}
    </div>
  );
}
